import { useMemo } from "react";
import { useFetch } from "@/hooks/useFetch";
import {
  fetchAverageFeedbackForCurrentUser,
  fetchDepartmentAverage,
  fetchGradeAverage,
  fetchSelfReviewScores,
} from "@/modules/performance/average-feedback/api";
import { fetchEmployeeById } from "@/modules/performance/team-performance/employee-reviews/api";

// Convert 1-7 score to percentage (0-100)
const toPercent = (score) => {
  if (score == null) return 0;
  const n = Number(score);
  if (Number.isNaN(n)) return 0;
  const clamped = Math.max(0, Math.min(7, n));
  return Math.round((clamped / 7) * 100);
};

export const useAverageFeedback = (quadId, employeeId) => {
  const { data: employeeData } = useFetch({
    queryKey: ["performance", "employee", employeeId],
    queryFn: () => fetchEmployeeById({ employeeId }),
    enabled: !!employeeId,
  });


  const grade = employeeData?.grade?.id;
  const department = employeeData?.department?.name;
  const avgQuery = useFetch({
    queryKey: ["performance", "average-feedback", quadId, employeeId],
    queryFn: () => fetchAverageFeedbackForCurrentUser({ quadId, employeeId }),
    enabled: !!quadId,
  });

  const selfQuery = useFetch({
    queryKey: ["performance", "self-review", quadId, employeeId],
    queryFn: () =>
      fetchSelfReviewScores({ quadrimesterId: quadId, employeeId }),
    enabled: !!quadId,
  });

  const roleAvgQuery = useFetch({
    queryFn: () =>
      fetchGradeAverage({ quadrimesterId: quadId, grade, employeeId }),
    enabled: !!quadId && !!grade,
  });

  const deptAvgQuery = useFetch({
    queryKey: ["performance", "dept-avg", quadId, department, employeeId],
    queryFn: () =>
      fetchDepartmentAverage({
        quadrimesterId: quadId,
        department,
        employeeId,
      }),
    enabled: !!quadId && !!department,
  });

  const isLoading =
    avgQuery.isLoading ||
    selfQuery.isLoading ||
    roleAvgQuery.isLoading ||
    deptAvgQuery.isLoading;

  const isError =
    avgQuery.isError ||
    selfQuery.isError ||
    roleAvgQuery.isError ||
    deptAvgQuery.isError;

  const reviewersAvg = avgQuery.data || {};
  const selfScores = selfQuery?.data || {};
  const roleAvg = roleAvgQuery?.data || {};
  const deptAvg = deptAvgQuery?.data || {};

  const getMetric = useMemo(
    () => (key) => ({
      self: toPercent(selfScores?.[key]),
      team: toPercent(deptAvg?.avg?.[key] ?? deptAvg?.[key]),
      grade: toPercent(roleAvg?.avg?.[key] ?? roleAvg?.[key]),
      reviewers: toPercent(reviewersAvg?.[key]),
    }),
    [selfScores, deptAvg, roleAvg, reviewersAvg],
  );

  return {
    isLoading,
    isError,
    getMetric,
    employee: employeeData,

    data: {
      reviewersAvg,
      selfScores,
      roleAvg,
      deptAvg,
    },
  };
};
